import type { ReactNode } from 'react';
import {
  Anchor,
  Box,
  Divider,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { CoachCohortParticipant } from '../../../services/api/coachPortal';
import { GroupBadge, ParticipantAvatar } from '../../../components/common';
import {
  renderAttendance,
  renderStatus,
} from '../../cohorts/components/participants/ParticipantCellRenderers';
import { formatDate } from '../../../utils/formatters';

// users.first_name and last_name can be null; never render "null".
function fullName(p: CoachCohortParticipant): string {
  return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
}

/** One labelled value. An empty value shows "Not provided". */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4} miw={0}>
      <Text
        fz={11}
        fw={600}
        c="dimmed"
        tt="uppercase"
        style={{ letterSpacing: '0.5px' }}
      >
        {label}
      </Text>
      {children}
    </Stack>
  );
}

function TextValue({ value }: { value: string | null }) {
  if (!value) {
    return (
      <Text fz={14} c="dimmed">
        Not provided
      </Text>
    );
  }
  // Long answers keep their line breaks and wrap inside the modal.
  return (
    <Text fz={14} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      {value}
    </Text>
  );
}

interface CoachParticipantDetailModalProps {
  opened: boolean;
  // Kept after close, so the content stays during the close animation.
  participant: CoachCohortParticipant | null;
  onClose: () => void;
}

/**
 * MED-1367: read-only detail for one participant in the coach table. It
 * shows only the row the coach API already returned, so it makes no extra
 * calls. The staff ParticipantDetailModal is not used: its endpoints are
 * staff-only.
 */
export function CoachParticipantDetailModal({
  opened,
  participant,
  onClose,
}: CoachParticipantDetailModalProps) {
  const isPhone = useMediaQuery('(max-width: 48em)');
  const p = participant;
  const name = p ? fullName(p) || p.email : '';

  return (
    <Modal
      opened={opened && p !== null}
      onClose={onClose}
      title={
        <Text fw={600} fz={16}>
          Participant details
        </Text>
      }
      size="lg"
      fullScreen={isPhone}
      centered={!isPhone}
    >
      {p && (
        <Stack gap="lg">
          <Group gap="md" wrap="nowrap" align="flex-start">
            <ParticipantAvatar
              firstName={p.first_name ?? ''}
              lastName={p.last_name ?? ''}
              size={48}
              style={p.status === 'withdrawn' ? { opacity: 0.5 } : undefined}
            />
            <Stack gap={4} miw={0}>
              <Text fw={600} fz={18} style={{ overflowWrap: 'anywhere' }}>
                {name}
              </Text>
              {p.preferred_name && p.preferred_name !== p.first_name && (
                <Text fz={13} c="dimmed">
                  Goes by {p.preferred_name}
                </Text>
              )}
            </Stack>
          </Group>

          <Divider />

          <SimpleGrid
            cols={{ base: 1, xs: 2 }}
            spacing="md"
            verticalSpacing="md"
          >
            <Field label="Email">
              <Anchor
                href={`mailto:${p.email}`}
                fz={14}
                style={{ overflowWrap: 'anywhere' }}
              >
                {p.email}
              </Anchor>
            </Field>
            <Field label="Role">
              <TextValue value={p.role} />
            </Field>
            <Field label="Business unit">
              <TextValue value={p.business_unit} />
            </Field>
            <Field label="Location">
              <TextValue value={p.location} />
            </Field>
            <Field label="Group">
              <Box>
                <GroupBadge groupName={p.group_name} groupId={p.group_id} />
              </Box>
            </Field>
            <Field label="Status">
              <Box>
                {renderStatus(p.status)}
                {p.status === 'withdrawn' && (
                  <Text fz={13} c="dimmed" mt={4}>
                    {p.withdrawn_at
                      ? `Withdrew on ${formatDate(p.withdrawn_at)}`
                      : 'Withdrawal date not recorded'}
                  </Text>
                )}
              </Box>
            </Field>
            <Field label="Attendance">{renderAttendance(p)}</Field>
            <Field label="Sessions attended">
              <Text fz={14}>
                {p.sessions_attended} of {p.total_sessions} marked
              </Text>
            </Field>
          </SimpleGrid>

          <Divider />

          <Field label="Experience">
            <TextValue value={p.experience} />
          </Field>
          <Field label="Value">
            <TextValue value={p.primary_goal} />
          </Field>
        </Stack>
      )}
    </Modal>
  );
}
